import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { Outlet } from 'react-router-dom';
import { useMediaQuery, Fab } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { useDispatch, useSelector } from "react-redux";
import { Footer } from '../components/footer';
import { DashboardNavbar } from '../components/dashboard-navbar';
import { DashboardSidebar } from '../components/dashboard-sidebar';
import { LogSidebar } from '../components/log-sidebar';
import { useSettings } from '../contexts/settings-context';
import { CommentDialog } from '../components/quick-actions/comment-dialog';
import { unpinSidebar } from '../slices/logSlice';
import { closeCommentDialog } from '../slices/commentSlice';
import { closeDocumentDialog } from '../slices/documentSlice';
import { fetchDeactivation } from '../slices/deactivationSlice';
import { RootState } from '../store';
import { DocumentDialog } from '../components/quick-actions/document-dialog';
import { DocumentsDrawer } from '../components/quick-actions/documents-drawer';
import { CommentsDrawer } from '../components/quick-actions/comments-drawer';
import { useAuth } from '../hooks/use-auth';
import { WelcomeDialog } from '../components/dialogs/welcome-dialog';
import { VideoTutorialDialog } from '../components/dialogs/video-tutorial-dialog';
import { isIntroVideoSeen, getVideoById, isPlaceholderVideo } from '../lib/video-tutorials';
import { useCompanySetup } from '../hooks/use-company-setup';
import { CompanySetupDialog } from '../components/company/company-setup-dialog';
import { OnboardingWidget } from '../components/onboarding/onboarding-widget';
import { OnboardingTourHost } from '../components/onboarding/onboarding-tour-host';
import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import { ChatWidget } from '../components/assistant/chat-widget';

const DashboardLayoutRoot = styled('div')(
  ({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    height: '100%',
    paddingTop: 64,
    maxWidth: '100%'
  })
);

const DashboardLayoutContent = styled('div')(
  () => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '100%',
  })
);

export const DashboardLayout: FC = () => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { settings, saveSettings } = useSettings();
  const { logSidebarPinned } = useSelector((state: RootState) => state.log)
  const { commentDialogOpen, commentDrawerOpen } = useSelector((state: RootState) => state.comment)
  const { documentDialogOpen, documentDrawerOpen } = useSelector((state: RootState) => state.document)
  const anyDrawerOpen = commentDrawerOpen || documentDrawerOpen;
  const { welcomeMessageRead } = useAuth();
  const { hasCompany, showSetupDialog, setShowSetupDialog, onCompanyCreated } = useCompanySetup();
  const dispatch = useDispatch();

  const [chatOpen, setChatOpen] = useState(false);
  const [showIntroVideo, setShowIntroVideo] = useState(false);
  useEffect(() => {
    const intro = getVideoById(0);
    if (intro && !isPlaceholderVideo(intro) && !isIntroVideoSeen()) {
      setShowIntroVideo(true);
    }
  }, []);

  // Load the field/tab deactivation sets for the active project once the
  // dashboard shell mounts (switching project triggers a full reload, so a
  // mount-time fetch is sufficient).
  useEffect(() => {
    dispatch(fetchDeactivation() as any);
  }, [dispatch]);

  // useEffect(() => {
  //   window.onbeforeunload = function() {return "Do you really want to leave our brilliant application?"};
  // }, []);

  const handlePinSidebar = (): void => {
    saveSettings({
      ...settings,
      pinSidebar: !settings.pinSidebar
    });
  };

  const handleOnPinLogSidebar = (): void => {
    dispatch(unpinSidebar());
  }

  const handleChatClick = () => {
    setChatOpen((prev) => !prev);
  };

  return (
    <DashboardLayoutRoot sx={{ backgroundColor: 'background.paper' }}>
      <DashboardNavbar />
      {!mdDown && (
        <DashboardSidebar
          onPin={handlePinSidebar}
          pinned={settings.pinSidebar}
        />
      )}
      <LogSidebar
        onPin={handleOnPinLogSidebar}
        pinned={logSidebarPinned}
      />
      <DashboardLayoutContent
        sx={{
          ml: {
            xs: 0,
            md: settings.pinSidebar ? '250px' : '73px'
          },
          mr: {
            xs: 0,
            md: logSidebarPinned ? '350px' : '0px'
          },
          backgroundColor: 'background.paper'
        }}
      >
        {/* <Box sx={{ height: 320, transform: 'translateZ(0px)', flexGrow: 1 }}> */}
        {/* <SpeedDial
            ariaLabel="SpeedDial basic example"
            sx={{ position: 'absolute', bottom: 25, right: 25 }}
            icon={<SpeedDialIcon />}
          >
            {actions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={() => {
                  console.log('dispatch')
                  dispatch(action.action);
                }}
              />
            ))}
          </SpeedDial> */}
        {/* </Box> */}
        <Outlet />
        <Footer />
        {/* <UnknownDialog open={unknownDialogOpen} onClose={() => dispatch(closeUnknownDialog())} /> */}
        {/* <FactorDialog open={factorDialogOpen} onClose={() => dispatch(closeFactorDialog())} /> */}
        {/* <IntellectualPropertyDialog open={intellectualPropertyDialogOpen} onClose={() => dispatch(closeIntellectualPropertyDialog())} /> */}
        {!anyDrawerOpen && (
          <Fab
            color="primary"
            aria-label="chat"
            onClick={handleChatClick}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1301
            }}
          >
            {chatOpen ? <CloseIcon /> : <ChatIcon />}
          </Fab>
        )}
        <ChatWidget open={chatOpen} onClose={() => setChatOpen(false)} />
        <OnboardingWidget hasCompany={hasCompany} />
        <OnboardingTourHost />
        <CommentDialog open={commentDialogOpen} onClose={() => dispatch(closeCommentDialog())} />
        <DocumentDialog open={documentDialogOpen} onClose={() => dispatch(closeDocumentDialog())} />
        <DocumentsDrawer />
        <CommentsDrawer />
        {/* <MoveDialog open={moveDialogOpen} onClose={() => dispatch(closeMoveDialog())} /> */}
        {/* <StakeholderDialog open={stakeholderDialogOpen} onClose={() => dispatch(closeStakeholderDialog())} /> */}
        <WelcomeDialog open={!welcomeMessageRead} onClose={() => { }} />
        <VideoTutorialDialog
          open={showIntroVideo}
          onClose={() => setShowIntroVideo(false)}
          initialVideoId={0}
          isIntroFlow
        />
        <CompanySetupDialog
          open={showSetupDialog}
          onClose={() => setShowSetupDialog(false)}
          onSuccess={onCompanyCreated}
        />
      </DashboardLayoutContent>
    </DashboardLayoutRoot>
  );
};
